import React, { Component, Fragment } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Typography,
} from "@material-ui/core";
import moment from "moment";
import agent from "../../agent";

const HOT_PINK = "#fe0d9d";

class HGSuperPayBillingHistory extends Component {
    state = {
        invoices: null,
        loading: true,
        error: false,
    };

    componentDidMount() {
        this._load();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.profileId !== this.props.profileId) {
            this._load();
        }
    }

    _load() {
        const { profileId } = this.props;
        if (!profileId) {
            this.setState({ loading: false, invoices: [], error: false });
            return;
        }
        this.setState({ loading: true, error: false });
        agent.HgSuperPayAddon.getBillingHistory(profileId, 50)
            .then(response => {
                const data = response?.data || response;
                this.setState({ loading: false, invoices: data?.invoices || [] });
            })
            .catch(() => {
                this.setState({ loading: false, error: true });
            });
    }

    renderLoading() {
        return (
            <div style={{ padding: "60px 0", display: "flex", justifyContent: "center" }}>
                <CircularProgress style={{ color: HOT_PINK }} />
            </div>
        );
    }

    renderError() {
        return (
            <div style={{ textAlign: "center", padding: "60px 24px" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#333", marginBottom: 8 }}>
                    Could not load billing history
                </div>
                <div style={{ fontSize: 13.5, color: "#888", marginBottom: 20 }}>
                    An error occurred while fetching invoices. Please try again.
                </div>
                <Button
                    variant="contained"
                    style={{ backgroundColor: HOT_PINK, color: "#fff" }}
                    onClick={() => this._load()}
                >
                    Retry
                </Button>
            </div>
        );
    }

    renderEmpty() {
        return (
            <div style={{ textAlign: "center", padding: "60px 24px" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🧾</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#333", marginBottom: 8 }}>
                    No invoices yet
                </div>
                <div style={{ fontSize: 13.5, color: "#888" }}>
                    Invoices will appear here once the venue's subscription has been billed.
                </div>
            </div>
        );
    }

    renderStatusBadge(status) {
        const map = {
            paid:   { bg: "#e8f7ef", color: "#15a36a", label: "Paid" },
            open:   { bg: "#fff8e1", color: "#b97400", label: "Open" },
            void:   { bg: "#f5f5f5", color: "#888",    label: "Void" },
            draft:  { bg: "#f0f0f0", color: "#555",    label: "Draft" },
        };
        const s = map[status] || { bg: "#f5f5f5", color: "#888", label: status || "Unknown" };
        return (
            <span style={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 9px",
                borderRadius: 999,
                background: s.bg,
                color: s.color,
                letterSpacing: ".03em",
            }}>
                {s.label}
            </span>
        );
    }

    render() {
        const { loading, error, invoices } = this.state;

        if (loading) return this.renderLoading();
        if (error)   return this.renderError();
        if (!invoices || invoices.length === 0) return this.renderEmpty();

        return (
            <Fragment>
                <div style={{ overflowX: "auto" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 700, color: "#555", fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em" }}>
                                    #
                                </TableCell>
                                <TableCell style={{ fontWeight: 700, color: "#555", fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em" }}>
                                    Date
                                </TableCell>
                                <TableCell style={{ fontWeight: 700, color: "#555", fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em" }}>
                                    Period
                                </TableCell>
                                <TableCell style={{ fontWeight: 700, color: "#555", fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em" }}>
                                    Amount
                                </TableCell>
                                <TableCell style={{ fontWeight: 700, color: "#555", fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em" }}>
                                    Status
                                </TableCell>
                                <TableCell style={{ fontWeight: 700, color: "#555", fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em" }}>
                                    Invoice
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.map((inv, i) => {
                                const date = inv.date ? moment.unix(inv.date).local().format("DD MMM YYYY") : (inv.dateFormatted ? moment(inv.dateFormatted).format("DD MMM YYYY") : "—");
                                const periodStart = inv.periodStart ? moment.unix(inv.periodStart).local().format("DD MMM YYYY") : "—";
                                const periodEnd   = inv.periodEnd   ? moment.unix(inv.periodEnd).local().format("DD MMM YYYY")   : "—";
                                const amount = inv.amountFormatted
                                    ? `A$ ${inv.amountFormatted}`
                                    : inv.amountPaid != null
                                    ? `A$ ${(inv.amountPaid / 100).toFixed(2)}`
                                    : "—";

                                return (
                                    <TableRow key={inv.invoiceId || i} hover>
                                        <TableCell style={{ color: "#888", fontSize: 13 }}>{i + 1}</TableCell>
                                        <TableCell style={{ fontSize: 13.5 }}>{date}</TableCell>
                                        <TableCell style={{ fontSize: 13, color: "#666" }}>
                                            {periodStart} – {periodEnd}
                                        </TableCell>
                                        <TableCell style={{ fontSize: 13.5, fontWeight: 600 }}>{amount}</TableCell>
                                        <TableCell>{this.renderStatusBadge(inv.status)}</TableCell>
                                        <TableCell>
                                            {inv.invoicePdf ? (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    href={inv.invoicePdf}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ borderColor: HOT_PINK, color: HOT_PINK, fontSize: 12, textTransform: "none" }}
                                                >
                                                    PDF
                                                </Button>
                                            ) : inv.invoiceUrl ? (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    href={inv.invoiceUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ borderColor: "#aaa", color: "#666", fontSize: 12, textTransform: "none" }}
                                                >
                                                    View
                                                </Button>
                                            ) : (
                                                <span style={{ color: "#ccc", fontSize: 12 }}>—</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </Fragment>
        );
    }
}

export default HGSuperPayBillingHistory;
